import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {
  CheckIcon,
  AlertCircleIcon,
} from "lucide-react";
import { applyForJob } from "@service/job";
import { parseFormat } from "@util/date";
import { Job, JobApplication } from "@type/job";
import { trackEvent } from "@util/mixpanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@component/select";
import { Input } from "@component/input";

dayjs.extend(customParseFormat);

interface Props {
  job: Job | undefined;
  onComplete: (jobApplication: JobApplication) => void;
  onBack?: () => void;
}

export default function WorkAuthorization(props: Props) {
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [country, setCountry] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [noSponsorshipRequired, setNoSponsorshipRequired] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (props.job && !props.job?.is_enable_work_authorization) {
      setLoading(true);

      // Call the applyForJob service with the job ID
      applyForJob({
        job_id: props.job?.id || "",
        referral_code: sessionStorage.getItem("__referral_code__") || undefined,
      }).then((response) => {
        if (response) {
          trackEvent("job_application_submitted", {
            job_id: props.job?.id || "",
            job_title: props.job?.job_title || "",
            company_name: props.job?.company_name || "",
            job_referral_code: sessionStorage.getItem("__referral_code__") || "",
            enabled_work_authorization: props.job?.is_enable_work_authorization,
          });
          props.onComplete(response);
        } else {
          setSubmitError("Failed to submit application. Please try again.");
        }
      }).catch((error) => {
        console.error("Error applying for job:", error);
        setSubmitError("An error occurred while submitting your application.");
      }).finally(() => {
        setLoading(false);
      })
    }
  }, [props.job])

  // List of countries for the dropdown
  const countries = [
    "China",
    "Hong Kong",
    "India",
    "Indonesia",
    "Japan",
    "Korea",
    "Malaysia",
    "Philippines",
    "Singapore",
    "Thailand",
    "United States",
    "Vietnam"
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!dateOfBirth || !dayjs(dateOfBirth, "YYYY-MM-DD", true).isValid()) {
      newErrors.dateOfBirth = "Please enter a valid date of birth.";
    } else {
      const parsed = dayjs(dateOfBirth, "YYYY-MM-DD", true);
      if (parsed.year() < 1900) {
        newErrors.dateOfBirth = "Please enter a valid date of birth.";
      } else if (parsed.isAfter(dayjs())) {
        newErrors.dateOfBirth = "Date of birth cannot be in the future.";
      }
    }

    if (!country) {
      newErrors.country = "Country of residence is required.";
    }

    if (!authorized) {
      newErrors.authorized = "You must confirm you are authorized to work.";
    }

    if (!noSponsorshipRequired) {
      newErrors.noSponsorshipRequired =
        "You must confirm you don't require sponsorship.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    if (validateForm()) {
      setLoading(true);

      try {
        // Call the applyForJob service with the job ID
        const response = await applyForJob({
          job_id: props.job?.id || "",
          country_of_residence: country,
          date_of_birth: parseFormat(dateOfBirth, "YYYY-MM-DD"),
          do_not_need_visa: !!noSponsorshipRequired,
          legally_authorized: !!authorized,
          referral_code: sessionStorage.getItem("__referral_code__") || undefined,
        });

        if (response) {
          trackEvent("job_application_submitted", {
            job_id: props.job?.id || "",
            job_title: props.job?.job_title || "",
            company_name: props.job?.company_name || "",
            job_referral_code: sessionStorage.getItem("__referral_code__") || "",
            work_auth_provided: !!authorized
          });
          props.onComplete(response);
        } else {
          setSubmitError("Failed to submit application. Please try again.");
        }
      } catch (error) {
        console.error("Error applying for job:", error);
        setSubmitError("An error occurred while submitting your application.");
      } finally {
        setLoading(false);
      }
    }
  };

  if (!props.job?.is_enable_work_authorization) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 sm:p-6">
      <div className="mb-4 sm:mb-6">
        <h2 className="text-2xl font-bold">Work Authorization</h2>
        <p className="text-gray-600 mt-1">
          Please provide information about your work authorization status.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-gray-50 rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth
            </label>
            <Input
              id="dateOfBirth"
              placeholder="YYYY-MM-DD"
              value={dateOfBirth}
              onChange={(e) => {
                const value = e.target.value;
                // Allow only numbers and hyphens
                const sanitized = value.replace(/[^\d-]/g, '');

                // Format as YYYY-MM-DD
                let formatted = sanitized;
                if (sanitized.length >= 4 && sanitized[4] !== '-') {
                  formatted = sanitized.slice(0, 4) + '-' + sanitized.slice(4);
                }
                if (formatted.length >= 7 && formatted[7] !== '-') {
                  formatted = formatted.slice(0, 7) + '-' + formatted.slice(7);
                }

                // Limit to 10 characters (YYYY-MM-DD)
                formatted = formatted.slice(0, 10);

                setDateOfBirth(formatted);
              }}
              pattern="\d{4}-\d{2}-\d{2}"
              maxLength={10}
            />
            {errors.dateOfBirth && (
              <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country of Residence
            </label>
            <Select
              value={country}
              onValueChange={(value: string) => setCountry(value)}
            >
              <SelectTrigger className="w-full border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 dark:bg-gray-800 pt-2 pb-2 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countries.map((item, index) => (
                  <SelectItem key={index} value={item}>{item}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.country && (
              <p className="mt-1 text-sm text-red-600">{errors.country}</p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <h3 className="text-lg font-medium mb-4">
            Authorization Confirmation
          </h3>

          <div className="space-y-4">
            <div
              className={`p-4 border rounded-lg ${errors.authorized
                ? "border-red-300 bg-red-50"
                : "border-gray-300"
                }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-4">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    checked={authorized}
                    onChange={(e) => setAuthorized(e.target.checked)}
                  />
                </div>

                <span>
                  <span className="text-sm font-medium text-gray-700">
                    I am legally authorized to work in the country where I am
                    physically located
                  </span>
                  {errors.authorized && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircleIcon className="h-4 w-4 mr-1" />
                      {errors.authorized}
                    </p>
                  )}
                </span>
              </div>
            </div>

            <div
              className={`p-4 border rounded-lg ${errors.noSponsorshipRequired
                ? "border-red-300 bg-red-50"
                : "border-gray-300"
                }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-4">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    checked={noSponsorshipRequired}
                    onChange={(e) => setNoSponsorshipRequired(e.target.checked)}
                  />
                </div>

                <span className="">
                  <span className="text-sm font-medium text-gray-700">
                    I do not require work visa sponsorship now or in the future
                  </span>
                  {errors.noSponsorshipRequired && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircleIcon className="h-4 w-4 mr-1" />
                      {errors.noSponsorshipRequired}
                    </p>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-[#E7FFE6] border border-[#C5F5C4] rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-4">
              <CheckIcon className="h-5 w-5 text-[#0B4E0A]" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-[#0B4E0A]">
                Important Information
              </h3>
              <div className="mt-2 text-sm text-[#0B4E0A]">
                <p>
                  By submitting this form, you certify that all information
                  provided is true and accurate. Providing false information may
                  result in the termination of your application or employment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>

      {submitError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {submitError}
        </div>
      )}

      <div className="flex justify-center">
        {props.onBack && (
          <button
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
            onClick={props.onBack}
            type="button"
          >
            Previous
          </button>
        )}
        <button
          className={`px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md transition-colors flex items-center w-full sm:w-auto justify-center`}
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Submitting...
            </>
          ) : (
            "Submit Application"
          )}
        </button>
      </div>
    </div>
  );
}
